import PictureInPictureIcon from "@mui/icons-material/PictureInPicture";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { useTheme, type SxProps } from "@mui/material/styles";
import InputFormField from "components/input-form-field";
import { useCallback } from "react";
import {
  useWatch,
  type Control,
  type FieldValues,
  type Path,
  type PathValue,
  type UseFormSetValue,
} from "react-hook-form";
import ExpandableNotesDialog from "./expandable-notes-dialog.component";

type ExpandableNotesFieldProps<T extends FieldValues> = {
  showMemoDialog: boolean;
  setShowMemoDialog: React.Dispatch<React.SetStateAction<boolean>>;
  label: string;
  placeholder: string;
  name: Path<T>;
  control: Control<T, string>;
  setValue: UseFormSetValue<T>;
  sx?: SxProps;
  templateTypes: number[];
  mergeTagsPatientId?: number;
  enableExpandButton?: boolean;
  expandButtonLabel?: string;
  patientNotSelectedWarning?: string;
};

export default function ExpandableNotesField<T extends FieldValues>({
  showMemoDialog,
  setShowMemoDialog,
  label,
  placeholder,
  name,
  control,
  setValue,
  sx,
  templateTypes,
  mergeTagsPatientId,
  enableExpandButton,
  expandButtonLabel,
  patientNotSelectedWarning,
}: ExpandableNotesFieldProps<T>) {
  const theme = useTheme();
  const invoiceMemo = useWatch({ control, name });

  const handleShowMemoDialog = useCallback(() => {
    setShowMemoDialog(true);
  }, [setShowMemoDialog]);

  const handleHideMemoDialog = useCallback(() => {
    setShowMemoDialog(false);
  }, [setShowMemoDialog]);

  const onChange = useCallback(
    (newValue: string) => {
      setValue(name, newValue as PathValue<T, Path<T>>);
    },
    [name, setValue]
  );

  return (
    <Box
      sx={{
        maxHeight: 256,
        overflowY: "auto",
        boxSizing: "border-box",
        position: "relative",
      }}
    >
      {enableExpandButton && (
        <Box
          sx={{
            position: "absolute",
            top: "-9px",
            right: 0,
            zIndex: theme.zIndex.snackbar + 1,
          }}
        >
          <Button
            size="small"
            endIcon={<PictureInPictureIcon />}
            onClick={() => {
              let newValue = invoiceMemo
                .replaceAll(/<br\s*\/?>/g, "\n")
                .replaceAll(/<[^>]+>/g, "");
              setValue(name, newValue as PathValue<T, Path<T>>);
              setShowMemoDialog(true);
            }}
          >
            {expandButtonLabel}
          </Button>
        </Box>
      )}
      <InputFormField
        name={name}
        label={label}
        placeholder={placeholder}
        multiline
        control={control}
        sx={{
          mb: 2,
          ml: 0,
          width: "100%",
          opacity: showMemoDialog ? 0 : 1,
          ...sx,
        }}
        minRows={1}
        onClick={() => {
          if (!enableExpandButton) {
            handleShowMemoDialog();
          }
        }}
      />
      <ExpandableNotesDialog
        show={showMemoDialog}
        title={label}
        placeholder={placeholder}
        templateTypes={templateTypes}
        mergeTagsPatientId={mergeTagsPatientId}
        onClose={handleHideMemoDialog}
        onSubmit={onChange}
        initialValue={invoiceMemo}
        patientNotSelectedWarning={patientNotSelectedWarning}
      />
    </Box>
  );
}
